import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useContextBudget } from '@/hooks/useContextBudget';

interface ContextBudgetIndicatorProps {
  /** Conteúdo atual do campo de texto */
  value: string;
  /** ID do modelo selecionado. Se ausente, usa o pior caso entre modelos recomendados. */
  modelId?: string | null;
  className?: string;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}k`;
  return String(tokens);
}

interface RingProps {
  ratio: number;
  colorClass: string;
}

const Ring = ({ ratio, colorClass }: RingProps) => {
  const size = 14;
  const stroke = 2.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(Math.max(ratio, 0), 1);
  const dash = circumference * clamped;

  return (
    <svg width={size} height={size} className={cn('shrink-0', colorClass)} aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        opacity={0.2}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${circumference}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 200ms ease-out' }}
      />
    </svg>
  );
};

export const ContextBudgetIndicator = ({ value, modelId, className }: ContextBudgetIndicatorProps) => {
  const { contextWindow, maxChars, modelLabel, isFallback } = useContextBudget(modelId);
  const charCount = value?.length ?? 0;
  const ratio = maxChars > 0 ? charCount / maxChars : 0;
  const overLimit = ratio > 1;
  const percent = ratio * 100;
  const percentLabel = percent < 1 && percent > 0
    ? '<1%'
    : `${percent >= 10 ? Math.round(percent) : percent.toFixed(1).replace('.0', '')}%`;

  const colorClass = overLimit || ratio > 0.9
    ? 'text-destructive'
    : ratio > 0.75
    ? 'text-amber-600 dark:text-amber-500'
    : 'text-tertiary';

  return (
    <div className={cn('flex items-center justify-end gap-1.5 text-xs mt-1.5', colorClass, className)}>
      {overLimit ? (
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
      ) : (
        <Ring ratio={ratio} colorClass={colorClass} />
      )}
      <span>
        {percentLabel} do contexto ·{' '}
        {isFallback ? modelLabel : `${modelLabel} suporta até ${formatTokens(contextWindow)} tokens`}
      </span>
      {overLimit && <span className="ml-1">— pode exceder o contexto do modelo</span>}
    </div>
  );
};
